<script setup lang="ts">
import ProfileEditSpotifyArtist from '~/components/shared/profile/edit/spotifyArtists.vue'
import { BtnOrange } from '~/components/ui/Buttons'
import VText from '~/components/ui/VText.vue'

import type { UserInfluencerState } from '~/stores/userInfluencer'
import type SpotifyArtist from '~/types/spotifyArtist'

type Props = {
  modelValue: UserInfluencerState
}
type Emits = {
  'update:model-value': [influencer: Partial<UserInfluencerState> | null]
  'request-done': []
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const artists = ref<SpotifyArtist[]>([])
const loading = ref(false)
const profileEditSpotifyArtist = ref<InstanceType<
  typeof ProfileEditSpotifyArtist
> | null>(null)

async function next() {
  if (loading.value) return

  loading.value = true
  const response =
    (await profileEditSpotifyArtist.value?.updateOnServer()) || {}

  emit('update:model-value', response)
  await nextTick()
  emit('request-done')
}

function skip() {
  if (!profileEditSpotifyArtist.value) return

  profileEditSpotifyArtist.value.updatedArtists = []
  next()
}
</script>

<template>
  <div class="tw-relative tw-grid tw-w-full tw-gap-8">
    <div class="tw-grid tw-gap-2">
      <VText cfg="sans/32/bold">
        {{ $t('influencer.create.favoriteArtists00') }}
      </VText>
      <VText cfg="sans/14/normal" color="gray-500">
        {{ $t('influencer.create.favoriteArtists01') }}
      </VText>
    </div>
    <ProfileEditSpotifyArtist
      ref="profileEditSpotifyArtist"
      :user-entity-id="modelValue.id"
      class="expanded"
      user-kind="influencer"
      @artists="artists = $event"
    />
    <div class="tw-grid tw-gap-2 tw-pt-4">
      <BtnOrange :disabled="!artists.length" :loading="loading" @click="next">
        {{ $t('common.next') }}
      </BtnOrange>
      <div class="skip" @click="skip">
        {{ $t('influencer.create.favoriteArtists02') }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.influencerCreateFavoritesWrapper {
  position: relative;
  width: 100%;
}

.title {
  font-size: var(--font-size-title-big);
  font-weight: 700;
  color: var(--color-black);
  margin-bottom: 4px;
}

.subtitle {
  font-size: var(--font-size-big);
  font-weight: 300;
  text-align: center;
  color: var(--color-masala-grey-h);
  margin-bottom: 64px;
}

.actionWrap {
  margin-top: 56px;
}

.skip {
  font-size: var(--font-size);
  font-weight: 600;
  color: var(--color-masala-grey-h);
  margin-top: 18px;
  text-align: center;
  cursor: pointer;
}
</style>
